import React from "react";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import Carousel from "react-material-ui-carousel";
import { FaTimes } from "react-icons/fa";


// `images` accepts either a plain url string, or `{ url, title, linkUrl }` —
// clicking a slide opens `linkUrl` in a new tab when set, otherwise zooms
// into the image in a modal, matching the gallery behavior this component
// already had before it gained title/link support for the stories carousel.
// Shows exactly one image at a time; navigation arrows/indicators are hidden
// entirely when there's only one image, since there's nothing to navigate
// to. `intervalSeconds` controls the autoplay delay (configurable from the
// backoffice for the stories carousel) — autoplay is disabled outright with
// a single image, since cycling back to the same slide is pointless.
//
// The underlying carousel library measures each slide's height exactly once,
// synchronously, the moment it becomes active — it does not re-measure once
// an <img> finishes downloading. Left to its own devices, an <img> with only
// `width:100%; height:auto` has no known height until its bytes arrive, so
// the library would capture a too-small height and never correct it. To
// avoid that, every image's natural size is preloaded first so each <img>
// can be given an explicit `aspect-ratio`, letting the browser reserve the
// correct box size the instant it lays out — before the image itself has
// loaded — so the one-time measurement is already correct.
const ImageCarousel = ({ images = [], intervalSeconds = 5 }) => {
    const [selectedImage, setSelectedImage] = React.useState(null);
    const [dimensions, setDimensions] = React.useState({});
    const open = Boolean(selectedImage);

    const normalizedImages = images.map((image) => (
        typeof image === "string" ? { url: image } : image
    ));
    const hasMultiple = normalizedImages.length > 1;

    React.useEffect(() => {
        normalizedImages.forEach(({ url }) => {
            if (!url || dimensions[url]) return;
            const img = new Image();
            img.onload = () => {
                setDimensions((prev) => ({
                    ...prev,
                    [url]: { width: img.naturalWidth, height: img.naturalHeight },
                }));
            };
            img.src = url;
        });
    }, [images]); // eslint-disable-line

    const allDimensionsLoaded = normalizedImages.every(({ url }) => Boolean(dimensions[url]));

    const handleImageClick = (image) => {
        if (image.linkUrl) {
            window.open(image.linkUrl, "_blank", "noreferrer");
        } else {
            setSelectedImage(image.url);
        }
    };

    if (normalizedImages.length === 0 || !allDimensionsLoaded) {
        return null;
    }

    return (
        <Box width="100%">
            <Carousel
                autoPlay={hasMultiple}
                stopAutoPlayOnHover={true}
                interval={intervalSeconds * 1000}
                swipe={hasMultiple}
                indicators={hasMultiple}
                navButtonsAlwaysVisible={hasMultiple}
                navButtonsAlwaysInvisible={!hasMultiple}
                cycleNavigation={hasMultiple}
            >
                {
                    normalizedImages.map((image, idx) => {
                        const dim = dimensions[image.url];
                        return (
                            <Box key={idx} width="100%" textAlign="center">
                                <img
                                    src={image.url}
                                    onClick={() => handleImageClick(image)}
                                    alt={image.title || ""}
                                    style={{
                                        width: "100%",
                                        height: "auto",
                                        maxHeight: "70vh",
                                        aspectRatio: dim ? `${dim.width} / ${dim.height}` : undefined,
                                        textAlign: "center",
                                        cursor: "pointer",
                                        display: "block",
                                        margin: "0 auto",
                                        objectFit: "contain",
                                    }}
                                />
                                {image.title ? <Typography variant="body2" mt={1}>{image.title}</Typography> : null}
                            </Box>
                        )
                    })
                }
            </Carousel>
            <Modal open={open} onClose={() => setSelectedImage(null)}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%',
                        height: '100%',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <IconButton onClick={() => setSelectedImage(null)} color="primary" sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <FaTimes />
                    </IconButton>
                    <img src={selectedImage} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </Box>
            </Modal>
        </Box>
    );
}

export default ImageCarousel;
